import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/react/react/node_modules/@primer/gatsby-theme-doctocat/src/components/layout.js";
import data from '../../packages/react/src/KeybindingHint/KeybindingHint.docs.json';
import { ActionList, Button, Text, Box } from '@primer/react';
import { KeybindingHint } from '@primer/react/drafts';
import { TrashIcon } from '@primer/octicons-react';
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const ComponentProps = makeShortcode("ComponentProps");
const ComponentChecklist = makeShortcode("ComponentChecklist");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <p>{`Use `}<inlineCode parentName="p">{`KeybindingHint`}</inlineCode>{` to make keyboard shortcuts discoverable. Can render visual keybinding hints in condensed (abbreviated) form or expanded form, and provides accessible alternative text for screen reader users.`}</p>
    <Box sx={{
      border: '1px solid',
      borderColor: 'border.default',
      borderRadius: 2,
      padding: 6,
      marginBottom: 3
    }} mdxType="Box">
  <ActionList sx={{
        width: 320
      }} mdxType="ActionList">
    <ActionList.Item>
      Move down
      <ActionList.TrailingVisual>
        <KeybindingHint keys="Mod+ArrowDown" mdxType="KeybindingHint" />
      </ActionList.TrailingVisual>
    </ActionList.Item>
    <ActionList.Item>
      Unsubscribe
      <ActionList.TrailingVisual>
        <KeybindingHint keys="i j" mdxType="KeybindingHint" />
      </ActionList.TrailingVisual>
    </ActionList.Item>
    <ActionList.Item variant="danger">
      <ActionList.LeadingVisual>
        <TrashIcon mdxType="TrashIcon" />
      </ActionList.LeadingVisual>
      Delete
      <ActionList.TrailingVisual>
        <KeybindingHint keys="Mod+Shift+Delete" mdxType="KeybindingHint" />
      </ActionList.TrailingVisual>
    </ActionList.Item>
  </ActionList>
    </Box>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import {KeybindingHint} from '@primer/react/drafts'
`}</code></pre>
    <h2>{`Examples`}</h2>
    <h3>{`Single keys`}</h3>
    <p>{`Use the `}<a parentName="p" {...{
        "href": "https://developer.mozilla.org/en-US/docs/Web/API/UI_Events/Keyboard_event_key_values"
      }}>{`full names of the keys as returned by `}<inlineCode parentName="a">{`KeyboardEvent.key`}</inlineCode></a>{`. Key names are case-insensitive.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "live noinline",
        "live": true,
        "noinline": true
      }}>{`render(
  <>
    <KeybindingHint keys="a" /> <br />
    <KeybindingHint keys="B" /> <br />
    <KeybindingHint keys="ArrowLeft" /> <br />
    <KeybindingHint keys="shift" />
  </>,
)
`}</code></pre>
    <h4>{`Special key names`}</h4>
    <p>{`Because the `}<inlineCode parentName="p">{`+`}</inlineCode>{` and space characters are used to build chords and sequences as described below, their names must be spelled out to be used as keys.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "live noinline",
        "live": true,
        "noinline": true
      }}>{`render(
  <>
    <KeybindingHint keys="Plus" /> <br />
    <KeybindingHint keys="Space" />
  </>,
)
`}</code></pre>
    <h3>{`Chords`}</h3>
    <p><em parentName="p">{`Chords`}</em>{` are multiple keys that are pressed at the same time. Combine keys in a chord with `}<inlineCode parentName="p">{`+`}</inlineCode>{`. Keys are automatically sorted into a standardized order so that modifiers come first.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "live noinline",
        "live": true,
        "noinline": true
      }}>{`render(
  <>
    <KeybindingHint keys="Alt+a" /> <br />
    <KeybindingHint keys="a+Alt" /> <br />
    <KeybindingHint keys="Control+Shift+ArrowUp" /> <br />
    <KeybindingHint keys="Meta+Shift+&" />
  </>,
)
`}</code></pre>
    <h4>{`Platform-dependent modifier`}</h4>
    <p>{`Typical chords use `}<inlineCode parentName="p">{`Command`}</inlineCode>{` on MacOS and `}<inlineCode parentName="p">{`Control`}</inlineCode>{` on other devices. To automatically render `}<inlineCode parentName="p">{`Command`}</inlineCode>{` or `}<inlineCode parentName="p">{`Control`}</inlineCode>{` based on the user's operating system, use the special key name `}<inlineCode parentName="p">{`Mod`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "live noinline",
        "live": true,
        "noinline": true
      }}>{`render(<KeybindingHint keys="Mod+Shift+X" />)
`}</code></pre>
    <h3>{`Sequences`}</h3>
    <p><em parentName="p">{`Sequences`}</em>{` are keys or chords that are pressed one after the other. Combine elements in a sequence with a space. For example, `}<inlineCode parentName="p">{`a b`}</inlineCode>{` means "press a, then press b".`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "live noinline",
        "live": true,
        "noinline": true
      }}>{`render(
  <>
    <KeybindingHint keys="a b" /> <br />
    <KeybindingHint keys="Mod+g ArrowLeft" />
  </>,
)
`}</code></pre>
    <h3>{`Full display format`}</h3>
    <p>{`The default `}<inlineCode parentName="p">{`condensed`}</inlineCode>{` format should be used on UI elements like buttons, menuitems, and inputs. In long-form text (prose), the `}<inlineCode parentName="p">{`full`}</inlineCode>{` variant can be used instead to help the text flow better.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "live noinline",
        "live": true,
        "noinline": true
      }}>{`render(
  <Text>
    Press <KeybindingHint keys="Mod+Enter" format="ful" /> to submit the form.
  </Text>,
)
`}</code></pre>
    <h3><inlineCode parentName="h3">{`onEmphasis`}</inlineCode>{` variant`}</h3>
    <p>{`When rendering on 'emphasis' colors, use the `}<inlineCode parentName="p">{`onEmphasis`}</inlineCode>{` variant.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "live noinline",
        "live": true,
        "noinline": true
      }}>{`const CmdEnterHint = () => <KeybindingHint variant="onEmphasis" keys="Mod+Enter" />

render(
  <Button variant="primary" trailingVisual={CmdEnterHint}>
    Submit
  </Button>,
)
`}</code></pre>
    <h2>{`Props`}</h2>
    <ComponentProps data={data} mdxType="ComponentProps" />
    <h2>{`Status`}</h2>
    <ComponentChecklist items={{
      propsDocumented: true,
      noUnnecessaryDeps: true,
      adaptsToThemes: true,
      adaptsToScreenSizes: true,
      fullTestCoverage: true,
      usedInProduction: true,
      usageExamplesDocumented: true,
      hasStorybookStories: true,
      designReviewed: false,
      a11yReviewed: false,
      stableApi: false,
      addressedApiFeedback: false,
      hasDesignGuidelines: false,
      hasFigmaComponent: false
    }} mdxType="ComponentChecklist" />

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      